"use client";

import type { VariantProps } from "class-variance-authority";
import type * as React from "react";

import { buttonVariants } from "@components/core/Button";
// TODO: migrate to react aria alternative
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";

const Tabs = TabsPrimitive.Root;

const tabListVariants = cva("flex items-center", {
  variants: {
    variant: {
      default: " gap-x-12",
      toggle: "bg-dark gap-x-4 rounded-full p-6",
      scroller: "breakout full-bleed-scroller scrollbar-none gap-0 border-b",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type TabListVariants = VariantProps<typeof tabListVariants>;

interface TabListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {}
const TabsList = ({
  className,
  variant,
  ref,
  ...props
}: TabListProps & { ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.List>> } & TabListVariants) => (
  <TabsPrimitive.List className={cn(tabListVariants({ variant, className }))} ref={ref} {...props} />
);

interface TabTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  inverse?: boolean;
  variant?: "default" | "scroller" | "toggle";
}
const TabsTrigger = ({
  className,
  inverse = false,
  variant = "default",
  ref,
  ...props
}: TabTriggerProps & { ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.Trigger>> }) => (
  <TabsPrimitive.Trigger
    className={cn(
      buttonVariants({
        variant: variant === "default" ? (inverse ? "outline-inverse" : "elevated") : "ghost",
      }),
      "w-fit",
      variant === "scroller"
        ? "rounded-none text-lighter hover:text-default data-[state=active]:border-b data-[state=active]:border-darkest data-[state=active]:text-default"
        : "",
      className,
    )}
    ref={ref}
    {...props}
  />
);

const TabsContent = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
  ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.Content>>;
}) => <TabsPrimitive.Content className={cn("", className)} ref={ref} {...props} />;

export { Tabs, TabsContent, TabsList, TabsTrigger };
